import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Spinner, ProgressBar, Button, Alert } from 'react-bootstrap';
import { getChunkStatus, discardPendingChunks } from '../../apiClient';
import './TranscriptionPendingModal.css';

/**
 * Modal som visas när chunks fortfarande transkriberas vid avslut.
 * Pollar chunk_status och ger användaren ett smart val när det drar ut på tiden:
 *  - < 10s: lugnt "transkriberar fortfarande".
 *  - >= 10s OCH bara svansen kvar (can_discard): välj att vänta ELLER generera
 *    journalen utan de sista sekunderna (kastar svans-chunken).
 *  - >= 10s OCH mer/hål kvar: lugnande "tar längre tid, låt stå öppet".
 *
 * @param {number} sessionId - Session ID att tracka (kan vara null tills lokal-
 *   först-uppladdaren skapat server-sessionen)
 * @param {number} localPending - Antal lokala chunkar som ännu inte bekräftats
 *   av servern (lokal-först-kö). Journalen får inte genereras förrän detta är 0.
 * @param {function} onComplete - Callback med { hasMorePending, discarded, deferredLocal }
 */
const SMART_MESSAGE_AFTER_SEC = 15;   // när "tar längre tid"-texten/valet dyker upp
const SAFETY_TIMEOUT_MS = 300000;     // 5 min säkerhetsventil

const TranscriptionPendingModal = ({ sessionId, localPending = 0, onComplete }) => {
  const [status, setStatus] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [userChoseWait, setUserChoseWait] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const pollFailuresRef = useRef(0);

  // Alltid färsk lokal-kö-räknare inne i poll-loopen (utan att starta om den).
  const localPendingRef = useRef(localPending);
  useEffect(() => { localPendingRef.current = localPending; }, [localPending]);

  const handleDiscard = useCallback(async () => {
    setDiscarding(true);
    try {
      await discardPendingChunks(sessionId);
    } catch (err) {
      console.error('Kunde inte kassera kvarvarande chunks:', err);
      // Fortsätt ändå — journalen genereras från befintlig text.
    }
    onComplete({ hasMorePending: false, discarded: true });
  }, [sessionId, onComplete]);

  const handleWait = useCallback(() => setUserChoseWait(true), []);

  useEffect(() => {
    let intervalId = null;
    let tickId = null;
    let timeoutId = null;

    const pollStatus = async () => {
      // Lokal-först: vänta tills alla lokala chunkar bekräftats av servern.
      // Först då är transkriptet komplett och journalen får genereras.
      if (localPendingRef.current > 0) return;
      // Server-sessionen kanske inte skapats än (servern var nere vid start) —
      // uppladdaren skapar den lazy; vänta tills vi har ett id att polla.
      if (!sessionId) return;

      try {
        const data = await getChunkStatus(sessionId);
        setStatus(data);
        pollFailuresRef.current = 0;

        if (data.all_processed) {
          clearInterval(intervalId);
          onComplete({ hasMorePending: data.has_pending_retries || false });
        }
      } catch (err) {
        console.error('Error polling chunk status:', err);
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current > 5) {
          clearInterval(intervalId);
          onComplete({ hasMorePending: false });
        }
      }
    };

    pollStatus();
    intervalId = setInterval(pollStatus, 2000);
    tickId = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      // Om lokala chunkar fortfarande väntar (servern ihållande nere) genererar
      // vi INTE en halv journal — inspelningen är säkrad lokalt och skickas när
      // servern är tillbaka. Signalera detta så MainContent visar lugnande info.
      onComplete({ hasMorePending: false, deferredLocal: localPendingRef.current > 0 });
    }, SAFETY_TIMEOUT_MS);

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (tickId) clearInterval(tickId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId, onComplete]);

  const hasProgress = status && status.total_chunks > 0;
  const canDiscard = !!(status && status.can_discard);
  const missingSeconds = status ? (status.missing_chunks || 1) * 30 : 30;
  // Efter ~15s: byt till lugnande "tar längre tid"-text. Erbjud discard-valet
  // bara om det bara är svansen kvar och användaren inte redan valt att vänta.
  const elapsedLong = elapsedSec >= SMART_MESSAGE_AFTER_SEC;
  const showDiscardChoice = elapsedLong && canDiscard && !userChoseWait;
  const MSG_TRANSCRIBING = 'Vi transkriberar din sista del. Så fort den är klar genereras journalen automatiskt.';
  const MSG_TAKING_LONGER = 'Det verkar ta lite längre tid än vanligt. Oroa dig inte — ditt arbete påverkas inte. Låt fönstret vara kvar så blir journalen klar av sig själv.';

  const progressBlock = hasProgress && (
    <>
      <p className="mb-2">
        {status.completed_chunks} av {status.total_chunks} delar klara
      </p>
      <ProgressBar
        now={status.progress_percent}
        label={`${status.progress_percent}%`}
        className="mb-3"
        style={{ height: '25px' }}
      />
    </>
  );

  return (
    <Modal show={true} backdrop="static" keyboard={false} centered className="transcription-pending-modal">
      <Modal.Body className="text-center py-4">
        {showDiscardChoice ? (
          /* Efter ~15s och bara svansen kvar → lugnande text + val */
          <>
            <h4 className="mb-3">Sista biten tar lite längre tid</h4>
            {progressBlock}
            <p className="text-muted mb-4">
              Det verkar ta lite längre tid än vanligt. Oroa dig inte — ditt arbete
              påverkas inte. Vill du vänta in sista biten, eller generera journalen
              nu utan de sista ~{missingSeconds} sekunderna?
            </p>
            <div className="d-flex gap-2 justify-content-center flex-wrap">
              <Button variant="primary" onClick={handleWait} disabled={discarding}>
                Vänta in sista biten
              </Button>
              <Button variant="outline-secondary" onClick={handleDiscard} disabled={discarding}>
                {discarding ? 'Genererar…' : `Generera utan de sista ~${missingSeconds} sek`}
              </Button>
            </div>
          </>
        ) : (
          /* Lugnt vänteläge — text beror på hur länge det tagit */
          <>
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h4 className="mb-3">
              {elapsedLong ? 'Det tar lite längre tid' : 'Transkriberar din sista del…'}
            </h4>
            {progressBlock}
            <p className="text-muted mb-0">
              {elapsedLong ? MSG_TAKING_LONGER : MSG_TRANSCRIBING}
            </p>
            {status && status.has_pending_retries && (
              <Alert variant="info" className="mt-3 mb-0 text-start">
                <small>{status.failed_chunks} del(ar) körs om i bakgrunden.</small>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TranscriptionPendingModal;
